import React from 'react'
import { Dropdown } from 'react-bootstrap'
import styled from 'styled-components/macro'
import {
  BasicDropdownMenu,
  BasicDropdownMenuItem,
} from 'src/components/Reusable/BasicDropdown/BasicDropdown.styles'
import { ReactComponent as VerticalEllipsesIcon } from 'src/components/Icons/assets/vertical-ellipses-icon.svg'
import { DropdownToggleButton } from 'src/components/Reusable/Buttons'
import { FlexAlign, FlexColumn } from '../Flex'

// Full-width, left-aligned button for use as the clickable content of a
// GridRowMenu item. Stretching to the full row width makes the entire menu
// row clickable rather than only the text label.
export const GridRowMenuButton = styled.button.attrs({ type: 'button' })`
  display: block;
  width: 100%;
  cursor: pointer;
  background: transparent;
  border: 0;
  padding: 0;
  text-align: left;
`

export type GridRowMenuItem = {
  key: string
  item?: React.ReactNode | string
  disabled?: boolean
  divider?: boolean
}

type GridRowMenuProps = {
  items: GridRowMenuItem[]
  id: string
  onToggle?: (isOpen: boolean) => void
  show?: boolean
  anchorEl?: React.ReactNode
  as?: React.ElementType
  dataTestId?: string
  /** Position the menu against the window, for a grid inside a scroll area
   * sized to its rows: there an absolute menu is clipped by the scroll area
   * when only a row or two are showing. */
  fixedMenu?: boolean
}

export const GridRowMenu = ({
  items,
  id,
  onToggle,
  show,
  anchorEl,
  as,
  dataTestId,
  fixedMenu = false,
}: GridRowMenuProps): JSX.Element => {
  return (
    <Dropdown
      id={`grid-row-menu-${id}`}
      onToggle={onToggle}
      show={show}
      data-testid={dataTestId}
    >
      <Dropdown.Toggle
        as={as ?? DropdownToggleButton}
        id={`grid-row-menu-toggle-${id}`}
      >
        {anchorEl ?? <VerticalEllipsesIcon />}
      </Dropdown.Toggle>
      {items.length > 0 && (
        // Mounted up front when fixed: a fixed menu first mounted on open sits
        // at the window's corner, since react-bootstrap only re-positions one
        // already mounted (as in NestedMenu).
        <BasicDropdownMenu
          $offset={1}
          {...(fixedMenu && {
            popperConfig: { strategy: 'fixed' },
            renderOnMount: true,
          })}
        >
          <FlexColumn gap={8} align={FlexAlign.FLEX_START} $padding={4}>
            {items.map((entry) =>
              entry.divider ? (
                <Dropdown.Divider
                  key={entry.key}
                  style={{ margin: 0, width: '100%' }}
                />
              ) : (
                <BasicDropdownMenuItem
                  eventKey={entry.key}
                  key={entry.key}
                  disabled={entry.disabled}
                >
                  {entry.item}
                </BasicDropdownMenuItem>
              ),
            )}
          </FlexColumn>
        </BasicDropdownMenu>
      )}
    </Dropdown>
  )
}
